import React, { Fragment, useState, useEffect } from "react";
import Breadcrumb from "../common/breadcrumb";
import { Container, Card, CardBody, Badge, Spinner } from "reactstrap";
import customAxios from "../../customAxios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getSingleOrderList } from "../../redux/actions/OrderActions";
import moment from "moment/moment";
import { ShoppingBag, ChevronRight, Bell } from "react-feather";

const orderStatusColor = (status) => {
	if (!status) return "secondary";
	const s = String(status).toLowerCase();
	if (["created", "pending"].includes(s)) return "warning";
	if (["confirmed", "processing", "shipped"].includes(s)) return "info";
	if (["delivered", "completed", "paid"].includes(s)) return "success";
	if (["cancelled", "failed", "rejected"].includes(s)) return "danger";
	return "secondary";
};

const paymentStatusColor = (status) => {
	if (!status) return "secondary";
	const s = String(status).toLowerCase();
	if (["pending", "cod"].includes(s)) return "warning";
	if (s === "paid" || s === "completed") return "success";
	if (["failed", "refunded"].includes(s)) return "danger";
	return "secondary";
};

const formatStatus = (str) => {
	if (!str) return "-";
	return String(str).charAt(0).toUpperCase() + String(str).slice(1);
};

const NotificationList = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [notifications, setNotifications] = useState({ data: [], count: 0 });
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchNotifications = async () => {
			try {
				const { data } = await customAxios.get("admin/notifications");
				setNotifications({ data: data?.data || [], count: data?.count ?? 0 });
			} catch (err) {
				setNotifications({ data: [], count: 0 });
			} finally {
				setLoading(false);
			}
		};
		fetchNotifications();
	}, []);

	const handleNotificationClick = (res) => {
		if (res?.screen !== "order") return;
		const rightId = res?.order?._id ?? res?.order_id;
		if (!rightId) return;
		dispatch(getSingleOrderList(rightId));
		navigate(`${process.env.PUBLIC_URL || ""}/order/orderCustomerDetails/${rightId}`);
	};

	const isOrderNotification = (res) => res?.screen === "order" && (res?.order ?? res?.order_id);

	return (
		<Fragment>
			<Breadcrumb title="Notifications" parent="Orders" />
			<Container fluid={true}>
				<Card className="overflow-hidden" style={{ border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
					<CardBody className="p-0">
						<div className="px-4 py-3 border-bottom bg-light">
							<h5 className="mb-0 d-flex align-items-center">
								<Bell size={20} className="me-2 text-primary" />
								All Notifications
								{!loading && notifications.data?.length > 0 && (
									<Badge color="primary" className="ms-2 rounded-pill">
										{notifications.data.length}
									</Badge>
								)}
							</h5>
						</div>

						{loading ? (
							<div className="text-center py-5">
								<Spinner color="primary" />
								<p className="text-muted mt-2 mb-0">Loading notifications...</p>
							</div>
						) : !notifications.data?.length ? (
							<div className="text-center py-5 px-3">
								<Bell size={48} className="text-muted mb-2" />
								<p className="text-muted mb-0">No notifications yet.</p>
							</div>
						) : (
							<div className="notification-list">
								{notifications.data.map((res, index) => {
									const order = res?.order;
									const clickable = isOrderNotification(res);
									const isLast = index === notifications.data.length - 1;
									return (
										<div
											key={res?._id || index}
											onClick={() => clickable && handleNotificationClick(res)}
											role={clickable ? "button" : undefined}
											className={`d-flex align-items-start p-4 border-bottom border-light notification-item ${clickable ? "clickable" : ""} ${isLast ? "border-bottom-0" : ""}`}
											style={{
												cursor: clickable ? "pointer" : "default",
												transition: "background-color 0.15s ease",
											}}
											onMouseEnter={(e) => clickable && (e.currentTarget.style.backgroundColor = "#f8f9fa")}
											onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
										>
											<div className="flex-shrink-0 me-3 mt-1">
												<div
													className="rounded-circle d-flex align-items-center justify-content-center"
													style={{
														width: 44,
														height: 44,
														backgroundColor: order ? "rgba(115, 102, 255, 0.12)" : "rgba(108, 117, 125, 0.15)",
													}}
												>
													<ShoppingBag size={22} style={{ color: order ? "#7366ff" : "#6c757d" }} />
												</div>
											</div>
											<div className="flex-grow-1 min-width-0">
												<div className="d-flex flex-wrap align-items-center gap-2 mb-1">
													<strong className="text-dark">{res?.name}</strong>
													{order && (
														<>
															<Badge color={orderStatusColor(order?.orderStatus)} className="rounded-pill">
																{formatStatus(order?.orderStatus)}
															</Badge>
															<Badge color={paymentStatusColor(order?.paymentStatus)} className="rounded-pill">
																Payment: {formatStatus(order?.paymentStatus)}
															</Badge>
														</>
													)}
												</div>
												{res?.description && (
													<p className="text-muted small mb-2">{res.description}</p>
												)}
												{order && (
													<div className="d-flex flex-wrap align-items-center gap-3 small text-muted">
														<span>Order #{order?.order_id ?? "-"}</span>
														{order?.orderDate && (
															<span>{moment(order.orderDate).format("DD MMM YYYY")}</span>
														)}
														{order?.total != null && (
															<span className="fw-semibold text-dark">BHD {Number(order.total).toFixed(2)}</span>
														)}
														{order?.billingAddress?.name && (
															<span>{order.billingAddress.name}</span>
														)}
														{order?.itemDetails?.length != null && (
															<span>{order.itemDetails.length} item(s)</span>
														)}
														{res?.created_at && (
															<span>{moment(res.created_at).format("DD MMM, HH:mm")}</span>
														)}
													</div>
												)}
												{res?.created_at && !order && (
													<span className="small text-muted">
														{moment(res.created_at).format("DD MMM YYYY, HH:mm")}
													</span>
												)}
											</div>
											{clickable && (
												<div className="flex-shrink-0 ms-2 mt-1">
													<ChevronRight size={20} className="text-muted" />
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</CardBody>
				</Card>
			</Container>
		</Fragment>
	);
};

export default NotificationList;
